import { Box, Typography } from "@outschool/backpack";
import { Activity as ActivityType } from "@outschool/gql-backend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import capitalize from "lodash/capitalize";
import React from "react";

import * as Activity from "../../../../../../shared/Activity";
import * as Routes from "../../../../../../shared/Routes";
import { getTrackingObject } from "../../../../../routes/TeacherProfile";
import Link from "../../../../Link";
import { scheduleText } from "../../../../listings/ActivityListing";
import { AgeRange, ClassImage, ClassTime, Title } from "../../UpcomingClass";
import { NormalizedEventInfo } from "./UpcomingClassesHelper";

type UpcomingClassProps = NormalizedEventInfo;

export const UpcomingClass: React.FunctionComponent<UpcomingClassProps> = ({
  section,
  activity,
  start,
  end,
  isFlex,
  sellerOrgLeaderName,
  sectionSequenceNumber,
}) => {
  const activityPath = React.useMemo(
    () => Routes.activityPath(activity),
    [activity],
  );

  const upcomingListingOnModalTrackingObject = React.useMemo(
    () =>
      getTrackingObject({
        area: "Upcoming Classes Modal",
        activity,
        section,
      }),
    [activity, section],
  );

  return (
    <Link
      to={activityPath}
      {...upcomingListingOnModalTrackingObject}
      useLinkTracking
    >
      <Box
        sx={{
          paddingBottom: "24px",
          borderBottom: "1px solid #DDDFE3",
        }}
      >
        <Box
          flex
          sx={{
            paddingTop: "24px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            <ClassTime
              start={start}
              end={end}
              isFlex={isFlex}
              formatOption="time"
            />

            <Title title={activity.title} />

            {sellerOrgLeaderName && (
              <SellerOrgLeaderName name={sellerOrgLeaderName} />
            )}
          </Box>

          <ClassImage photoUrl={activity.details.photo} />
        </Box>

        <Schedule
          activity={activity}
          meetingCount={section.meetingCount}
          sectionSequenceNumber={sectionSequenceNumber}
        />

        <AgeRange minAge={activity.age_min} maxAge={activity.age_max} />
      </Box>
    </Link>
  );
};

/** Schedule */
type ScheduleProps = {
  activity: Pick<
    ActivityType,
    | "duration_weeks"
    | "weekly_meetings"
    | "is_ongoing_weekly"
    | "isClub"
    | "hasTeacherSchedule"
    | "isSelfPaced"
  >;
  meetingCount: number;
  sectionSequenceNumber: number | null;
};

const Schedule: React.FunctionComponent<ScheduleProps> = ({
  activity,
  meetingCount,
  sectionSequenceNumber,
}) => {
  // scheduleText returns results in form ["Meets 3 times", "Over 1 week"] or ["Meets 3 times", undefined]
  const scheduleTextArray = React.useMemo(
    () => scheduleText(activity),
    [activity],
  );

  // Convert scheduleText as follows:
  // eg. ["Meets 3 times", "Over 1 week"] --> Meets 3 times over 1 week
  // eg. ["Meets 3 times", undefined] --> Meets 3 times
  const formattedSchedule = capitalize(
    scheduleTextArray
      .filter(e => e !== undefined)
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      .map(e => e.toLowerCase())
      .join(" "),
  );

  const meetingNumberInSequence =
    Activity.format(activity) === Activity.ACTIVITY_FORMAT.MULTI_DAY &&
    sectionSequenceNumber
      ? ` • ${sectionSequenceNumber} of ${meetingCount}`
      : null;

  return (
    <Typography
      variant="inherit"
      sx={{
        fontSize: "14px",
        lineHeight: "21px",
        color: "grey.500",
        display: "block",
        paddingTop: "8px",
      }}
    >
      {formattedSchedule}
      {meetingNumberInSequence}
    </Typography>
  );
};

/** Seller Org Leader Name */
type SellerOrgLeaderNameProps = {
  name: string;
};

const SellerOrgLeaderName: React.FunctionComponent<
  SellerOrgLeaderNameProps
> = ({ name }) => {
  return (
    <Typography
      variant="inherit"
      sx={{
        fontSize: "14px",
        lineHeight: "21px",
        color: "grey.500",
        display: "block",
      }}
    >
      Taught by {name}
    </Typography>
  );
};
